import type { TFunction } from "i18next";
import { ChevronRight } from "lucide-react";
import type { JSX, KeyboardEvent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { Badge } from "@/shared/ui/badge";

import { moduleLabel, resultIntent, resultLabel } from "../lib/labels";
import { describeEvent, targetLabel, targetTypeLabel } from "../lib/summary";
import { targetToHref } from "../lib/target-link";

type GlobalAuditEvent = components["schemas"]["GlobalAuditEvent"];

interface AuditTableProps {
  events: GlobalAuditEvent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onRowActivate: (eventId: number) => void;
}

const TH_CLASS =
  "h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap";
const TD_CLASS = "h-[52px] px-4 text-sm whitespace-nowrap";
// The Activity cell is the only wrapping column — it carries a full sentence.
const TD_ACTIVITY_CLASS = "min-w-[320px] max-w-[460px] px-4 py-2.5 align-top text-sm";
const CARD_CLASS = "overflow-x-auto rounded-[14px] border border-[var(--premium-border)]";

// Fixed locale so digit order/format is deterministic across environments
// (same approach as orders/lib/format.ts's Bangkok formatter) — but WITHOUT a
// fixed timeZone, so this genuinely renders in the viewer's local timezone.
// The full UTC instant is always preserved in the `title`/`dateTime` attrs.
const localDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const localTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const shortDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
});

function formatLocalDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${localDateFormatter.format(d)} ${localTimeFormatter.format(d)}`;
}

/**
 * A grouped row stands for N identical failures. Shows the clock time alone
 * when the group started today (the common case — a retry loop that began
 * this morning), and the date too once it has been running longer than that.
 */
function groupPill(
  t: TFunction,
  event: Pick<GlobalAuditEvent, "occurrences" | "first_seen">,
  now: Date = new Date(),
): { label: string; since: string } | null {
  const count = event.occurrences ?? 1;
  if (count <= 1 || event.first_seen === undefined) return null;
  const first = new Date(event.first_seen);
  if (Number.isNaN(first.getTime())) return null;

  const sameDay = localDateFormatter.format(first) === localDateFormatter.format(now);
  const since = sameDay
    ? localTimeFormatter.format(first)
    : `${shortDateFormatter.format(first)} ${localTimeFormatter.format(first)}`;
  return { label: t("group.pill", { count, since }), since };
}

export function AuditTable({
  events,
  isLoading,
  isError,
  onRetry,
  onRowActivate,
}: AuditTableProps): JSX.Element {
  const { t } = useTranslation("audit");

  if (isError) {
    return (
      <div
        className={CARD_CLASS}
        style={{ boxShadow: "var(--sh-card)" }}
        data-testid="audit-table-error"
      >
        <EmptyState
          title={t("error.title")}
          body={t("error.body")}
          action={
            <button
              type="button"
              onClick={onRetry}
              data-testid="audit-table-retry"
              className="inline-flex h-9 items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
            >
              {t("error.retry")}
            </button>
          }
        />
      </div>
    );
  }

  if (!isLoading && events.length === 0) {
    return (
      <div
        className={CARD_CLASS}
        style={{ boxShadow: "var(--sh-card)" }}
        data-testid="audit-table-empty"
      >
        <EmptyState title={t("empty.title")} body={t("empty.body")} />
      </div>
    );
  }

  return (
    <div
      className={CARD_CLASS}
      style={{ boxShadow: "var(--sh-card)" }}
      data-testid={isLoading ? "audit-table-loading" : "audit-table"}
    >
      <table className="min-w-full border-collapse">
        <thead className="sticky top-0 z-20 border-b border-[var(--row-divider)] bg-white">
          <tr>
            <th className={TH_CLASS}>{t("column.time")}</th>
            <th className={TH_CLASS}>{t("column.activity")}</th>
            <th className={TH_CLASS}>{t("column.module")}</th>
            <th className={TH_CLASS}>{t("column.target")}</th>
            <th className={TH_CLASS}>{t("column.actor")}</th>
            <th className={TH_CLASS}>{t("column.result")}</th>
            <th className={TH_CLASS}>
              <span className="sr-only">{t("column.view_details")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--row-divider)]">
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            : events.map((event) => (
                <Row
                  key={event.id}
                  event={event}
                  t={t}
                  onActivate={() => onRowActivate(event.id)}
                />
              ))}
        </tbody>
      </table>
    </div>
  );
}

function SkeletonRow(): JSX.Element {
  return (
    <tr className="animate-pulse" data-testid="audit-skeleton-row">
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} className={TD_CLASS}>
          <div className="h-3 w-24 rounded bg-[var(--surface-sunken)]" />
        </td>
      ))}
    </tr>
  );
}

interface RowProps {
  event: GlobalAuditEvent;
  t: TFunction;
  onActivate: () => void;
}

// The whole row is the sole click/keyboard target for the (Task 4) detail
// drawer — no second action menu, per the plan. A visible "View details" cue
// sits in the trailing cell so the row's affordance isn't purely implicit.
function Row({ event, t, onActivate }: RowProps): JSX.Element {
  const targetHref = targetToHref(event);
  const { title, summary, brief } = describeEvent(t, event);
  const occurrencesPill = groupPill(t, event);

  function handleKeyDown(e: KeyboardEvent<HTMLTableRowElement>): void {
    if (e.key === "Enter") {
      onActivate();
    }
    if (e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  }

  // Stops the row's own onActivate (opens the detail drawer) from also
  // firing — the target link is a nested, more specific action.
  function handleTargetClick(e: MouseEvent<HTMLAnchorElement>): void {
    e.stopPropagation();
  }

  return (
    <tr
      data-testid="audit-row"
      data-event-id={event.id}
      tabIndex={0}
      className="row-enter cursor-pointer transition-colors hover:bg-[var(--row-hover)] focus-ring"
      onClick={onActivate}
      onKeyDown={handleKeyDown}
    >
      <td className={`${TD_CLASS} text-[var(--text-secondary)] tabular-nums`}>
        {/* `title` alone isn't reliable accessible text (secondary-only
            exposure, hover/focus-only, ignored by mobile screen readers) —
            the sr-only span guarantees the UTC instant reaches assistive
            tech regardless of input method or verbosity setting. */}
        <time dateTime={event.occurred_at} title={event.occurred_at}>
          {formatLocalDateTime(event.occurred_at)}
          <span className="sr-only">
            {" "}
            {t("column.time_utc_suffix", { utc: event.occurred_at })}
          </span>
        </time>
      </td>
      <td className={TD_ACTIVITY_CLASS}>
        <div className="flex items-start gap-2">
          <span className="font-semibold text-[var(--text-primary)]" data-testid="audit-row-title">
            {title}
          </span>
          {occurrencesPill !== null && (
            <span
              data-testid="audit-row-occurrences"
              title={t("group.pill_title", {
                count: event.occurrences,
                since: occurrencesPill.since,
              })}
              className="mt-px shrink-0 rounded-full border border-[var(--intent-danger-border)] bg-[var(--intent-danger-bg)] px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-[var(--intent-danger-fg)]"
            >
              {occurrencesPill.label}
            </span>
          )}
        </div>
        {/* One line only — the full sentences live in the drawer; `title`
            carries them for hover. */}
        <p
          className="max-w-[420px] truncate text-[12.5px] text-[var(--text-secondary)]"
          title={summary}
          data-testid="audit-row-summary"
        >
          {brief}
        </p>
      </td>
      <td className={`${TD_CLASS} text-[var(--text-secondary)]`}>{moduleLabel(t, event.module)}</td>
      <td className={TD_CLASS}>
        {targetHref ? (
          <a
            href={targetHref}
            onClick={handleTargetClick}
            title={event.target_label}
            data-testid="audit-target-link"
            className="block max-w-[210px] truncate text-left font-semibold text-[var(--brand-600)] underline-offset-2 hover:underline focus-ring"
          >
            {targetLabel(event)}
          </a>
        ) : (
          <span
            className="block max-w-[210px] truncate font-semibold text-[var(--text-primary)]"
            title={event.target_label}
          >
            {targetLabel(event)}
          </span>
        )}
        <span className="block text-[11.5px] text-[var(--text-subtle)]">
          {targetTypeLabel(t, event.target_type)}
        </span>
      </td>
      <td className={TD_CLASS}>
        <span className="block max-w-[210px] truncate font-semibold text-[var(--text-primary)]">
          {event.actor_display_name}
        </span>
        {event.actor_email && (
          <span className="block max-w-[210px] truncate text-[11.5px] text-[var(--text-subtle)]">
            {event.actor_email}
          </span>
        )}
      </td>
      <td className={TD_CLASS}>
        <Badge intent={resultIntent(event.result)} dot data-testid="audit-result-badge">
          {resultLabel(t, event.result)}
        </Badge>
      </td>
      <td className={`${TD_CLASS} text-right`}>
        <span
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--brand-600)]"
          data-testid="audit-row-view-details"
        >
          {t("column.view_details")}
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </span>
      </td>
    </tr>
  );
}
